// Branding context. On load we GET /settings once and apply the tenant's accent
// color to a CSS variable (--brand) plus expose the welcome copy + logo. Settings
// are PUBLIC (no auth), so this resolves before/independent of sign-in. Missing or
// failed settings fall back to sensible defaults so the portal always renders.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getSettings } from "./api";
import type { PortalSettings } from "./types";

const DEFAULTS: PortalSettings = {
  branding: {
    primary_color: null,
    logo_url: null,
    secondary_color: null,
    company_name: null,
    support_email: null,
    favicon_url: null,
  },
  portal: { welcome: null },
  extra: {},
};

interface BrandingValue {
  settings: PortalSettings;
  loading: boolean;
  /** The effective accent color actually applied (resolved or default). */
  accent: string;
  /** Display name for the workspace (company_name, or "Help Center"). */
  name: string;
}

// Default accent = the ActionConnect brand emerald (matches styles.css --brand);
// a tenant's settings.branding.primary_color overrides it at runtime.
const FALLBACK_ACCENT = "#047857";

/** Accept #rgb / #rrggbb / rgb()/hsl()/named — reject anything that could break
 *  out of the CSS value context (no `;`, `{`, `}`, url(), etc.). */
function safeColor(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.trim();
  if (/[;{}()]/.test(v) && !/^(rgb|hsl)a?\([\d.,%\s/]+\)$/i.test(v)) return null;
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
  if (/^(rgb|hsl)a?\([\d.,%\s/]+\)$/i.test(v)) return v;
  if (/^[a-z]+$/i.test(v)) return v; // CSS named colors
  return null;
}

const BrandingContext = createContext<BrandingValue>({
  settings: DEFAULTS,
  loading: true,
  accent: FALLBACK_ACCENT,
  name: "Help Center",
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<PortalSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    getSettings(ctrl.signal)
      .then((res) => {
        if (!ctrl.signal.aborted) setSettings(res.settings ?? DEFAULTS);
      })
      .catch(() => {
        // Graceful default: a missing/failed /settings must not block the portal.
        if (!ctrl.signal.aborted) setSettings(DEFAULTS);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  const accent = safeColor(settings.branding.primary_color) ?? FALLBACK_ACCENT;
  const secondary = safeColor(settings.branding.secondary_color);
  const name = settings.branding.company_name?.trim() || "Help Center";

  // Apply the accent (+ optional secondary) to the document root so the whole
  // CSS kit picks it up.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand", accent);
    if (secondary) root.style.setProperty("--brand-2", secondary);
    return () => {
      root.style.removeProperty("--brand");
      if (secondary) root.style.removeProperty("--brand-2");
    };
  }, [accent, secondary]);

  // Reflect the workspace name in the tab title + swap the favicon when set.
  useEffect(() => {
    document.title = name === "Help Center" ? "Help Center" : `${name} · Help Center`;
  }, [name]);
  useEffect(() => {
    const href = settings.branding.favicon_url?.trim();
    if (!href) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const created = !link;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    const prev = link.href;
    link.href = href;
    return () => {
      if (created) link?.remove();
      else if (link) link.href = prev;
    };
  }, [settings.branding.favicon_url]);

  const value = useMemo<BrandingValue>(
    () => ({ settings, loading, accent, name }),
    [settings, loading, accent, name],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  return useContext(BrandingContext);
}
